import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { Address } from "viem";
import { registerL1Token, registerL2Token } from "../actions/registerToken";
import {
  allTokensQueryKey,
  bridgedTokensQueryKey,
  fetchSingleTokenWithChainCheck,
  type TokenDetails,
  tokenDetailsQueryKey,
} from "../queries/tokenReg";
import { getL2TokenRegByTokenAddr, storeL2TokenReg } from "../state/l2TokenRegParams";
import { storeTokenRegTxHash } from "../state/tokenRegTxHash";

export function useL1TokenRegMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ tokenAddress }: { tokenAddress: Address }) => {
      return registerL1Token(tokenAddress);
    },
    onSuccess: async (data, { tokenAddress }) => {
      storeL2TokenReg(data);
      storeTokenRegTxHash(tokenAddress, {
        l1RegisteredTxHash: data.txHash,
      });

      // Immediately fetch fresh data with chain check for this specific token
      const freshTokenData = await fetchSingleTokenWithChainCheck(tokenAddress);

      if (freshTokenData) {
        // Update the token-details cache
        queryClient.setQueryData<TokenDetails>(tokenDetailsQueryKey(tokenAddress), freshTokenData);

        // Update this token in the all-tokens cache
        const allTokens = queryClient.getQueryData<TokenDetails[]>(allTokensQueryKey());
        if (allTokens) {
          const updatedTokens = allTokens.map((t) =>
            t.tokenAddress.toLowerCase() === tokenAddress.toLowerCase() ? freshTokenData : t,
          );
          queryClient.setQueryData(allTokensQueryKey(), updatedTokens);
        }
      }

      // Invalidate to trigger any components using this data to re-render
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: tokenDetailsQueryKey(tokenAddress) }),
        queryClient.invalidateQueries({ queryKey: ["token_reg_tx_hash"] }),
      ]);

      toast.success("Token registered on L1!");
    },
    onError: (error) => {
      console.error(error);
      toast.error("Error registering the token on L1");
    },
  });
}

export function useL2TokenRegMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tokenAddress }: { tokenAddress: Address }) => {
      const l2RegParams = getL2TokenRegByTokenAddr(tokenAddress);
      if (!l2RegParams) throw new Error("No L2 token reg params found");

      // Get token info from cached data
      const allTokens = queryClient.getQueryData<
        Array<{ tokenAddress: Address; tokenName: string; tokenSymbol: string; tokenDecimals: number }>
      >(allTokensQueryKey());
      const token = allTokens?.find((t) => t.tokenAddress.toLowerCase() === tokenAddress.toLowerCase());

      if (!token) {
        throw new Error(`Token ${tokenAddress} not found in cached data`);
      }

      return registerL2Token(
        l2RegParams.tokenAddr,
        token.tokenName,
        token.tokenSymbol,
        token.tokenDecimals,
        l2RegParams.l2BlockNumber,
        l2RegParams.index,
      );
    },
    onSuccess: async (data, { tokenAddress }) => {
      storeTokenRegTxHash(tokenAddress, {
        l2RegisteredTxHash: data,
      });

      // Optimistically update the token to mark it as L2 registered
      const currentToken = queryClient.getQueryData<TokenDetails>(tokenDetailsQueryKey(tokenAddress));

      if (currentToken?.l1RegistrationStatus) {
        // Only update if L1 is already registered (confirming L2 registration)
        const updatedToken: TokenDetails = {
          ...currentToken,
          l2RegistrationStatus: true,
        };

        // Update token-details cache
        queryClient.setQueryData<TokenDetails>(tokenDetailsQueryKey(tokenAddress), updatedToken);

        // Update the token in all-tokens cache
        const allTokens = queryClient.getQueryData<TokenDetails[]>(allTokensQueryKey());
        if (allTokens) {
          const updatedAllTokens = allTokens.map((t) =>
            t.tokenAddress.toLowerCase() === tokenAddress.toLowerCase() ? updatedToken : t,
          );
          queryClient.setQueryData<TokenDetails[]>(allTokensQueryKey(), updatedAllTokens);
        }

        // Add to bridged tokens cache since it's now fully bridged
        const bridgedTokens = queryClient.getQueryData<TokenDetails[]>(bridgedTokensQueryKey());
        if (bridgedTokens && !bridgedTokens.find((t) => t.tokenAddress.toLowerCase() === tokenAddress.toLowerCase())) {
          queryClient.setQueryData<TokenDetails[]>(bridgedTokensQueryKey(), [...bridgedTokens, updatedToken]);
        }
      }

      // Only invalidate the tx hash query - don't invalidate token queries to preserve optimistic update
      await queryClient.invalidateQueries({ queryKey: ["token_reg_tx_hash"] });

      toast.success("Token registered on L2!");
    },
    onError: (error) => {
      console.error(error);
      toast.error("Error registering the token on L2");
    },
  });
}
